//dmlnqkTVv629iqAghlU8

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConversation } from '@elevenlabs/react';
import Webcam from 'react-webcam';
import {
  FiPhoneOff,
  FiSettings,
  FiAlertCircle,
  FiMail,
  FiLoader,
  FiClock,
  FiVideoOff,
  FiRefreshCw,
  FiBarChart,
  FiCalendar,
  FiTrendingUp,
  FiStar,
  FiPlay,
  FiMessageSquare,
  FiMic,
  FiMicOff,
  FiPause,
  FiSquare,
  FiHelpCircle,
  FiX,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import './Interview.css';
import logoShort from '../../assets/liwaza-short.png';
import logoShortWhite from '../../assets/liwaza-short-logo-white.png';
import {
  API_BASE_URL,
  ELEVEN_LABS_INTERVIEW_AGENT_ID,
  getAccountScopedLocalStorageKey,
  getAuthHeaders,
  toastdisplay,
  API_SOCKET_URL,
} from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
// Import voice minutes management
import {
  canStartSession,
  getMaxSessionDuration,
  recordSession,
  secondsToMinutes,
  getRemainingMinutes,
  formatMinutes,
  getSessionStats,
  getUserVoiceLimits,
  startActiveSession,
  getActiveSession,
  endActiveSession,
  getActiveSessionElapsedTime,
  getRemainingSessionTime,
  updateActiveSessionProgress,
  clearStaleSession,
} from '../../utils/voiceMinutesManager';
import VoiceMinutesWidget from '../../components/VoiceMinutesWidget';
import { renderMarkdownToSafeHtml } from '../../utils/safeHtml';

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const setScopedLocalStorageItem = (key, value) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  if (!scopedKey) return false;
  localStorage.setItem(scopedKey, value);
  return true;
};

const Interview = () => {
  const EMPTY_INTERVIEW_FEEDBACK_MESSAGE =
    "You didn't provide any verbal responses for the AI to evaluate. Try retaking the interview.";
  const INTERVIEW_FEEDBACK_ERROR_MESSAGE =
    'An error occurred while generating feedback. Please try again.';
  const [agentId] = useState(ELEVEN_LABS_INTERVIEW_AGENT_ID); // Replace with real agent ID
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [canStartInterview, setCanStartInterview] = useState(true);

  const [messages, setMessages] = useState([]);
  const [transcript, setTranscript] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const webcamRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  // Voice session management
  const [timeRemaining, setTimeRemaining] = useState(300); // Will be updated based on limits
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [maxSessionTime, setMaxSessionTime] = useState(0);

  // Dashboard states
  const [showDashboard, setShowDashboard] = useState(false);
  const [interviewHistory, setInterviewHistory] = useState([]);
  const [expandedLatestInterview, setExpandedLatestInterview] = useState(false);
  const [expandedInterviews, setExpandedInterviews] = useState(new Set());

  // Control Panel States
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const [agentVolume, setAgentVolume] = useState(100);
  const [searchParams] = useSearchParams();
  const school = searchParams.get('school');
  const program = searchParams.get('program');
  const [interviewGuide, setInterviewGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);

  const normalizeFeedbackItems = (value) => {
    const items = [];

    const pushItem = (item) => {
      if (typeof item !== 'string') {
        return;
      }

      const normalized = item
        .replace(/^\s*(?:[-•]|\*(?!\*))\s+/, '')
        .trim();
      if (normalized) {
        items.push(normalized);
      }
    };

    const collect = (input) => {
      if (!input) {
        return;
      }

      if (Array.isArray(input)) {
        input.forEach(collect);
        return;
      }

      if (typeof input === 'string') {
        const trimmed = input.trim();
        if (!trimmed) {
          return;
        }

        const segments = trimmed.includes('\n')
          ? trimmed.split(/\r?\n+/)
          : trimmed.includes(';')
            ? trimmed.split(/\s*;\s*/)
            : [trimmed];
        segments.forEach(pushItem);
        return;
      }

      if (typeof input === 'object') {
        Object.values(input).forEach(collect);
      }
    };

    collect(value);
    return [...new Set(items)];
  };

  const buildFeedbackSummary = (text) => {
    if (typeof text !== 'string') {
      return '';
    }

    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return '';
    }

    return normalized.length > 120
      ? `${normalized.substring(0, 117).trim()}...`
      : normalized;
  };

  const buildInterviewFeedbackMarkdown = (feedbackResponse) => {
    if (!feedbackResponse || typeof feedbackResponse !== 'object') {
      return '';
    }

    const summary =
      [
        feedbackResponse.summary,
        feedbackResponse.feedback_summary,
        feedbackResponse.feedbackSummary,
        feedbackResponse.assessment?.summary,
        feedbackResponse.assessment?.feedbackSummary,
      ].find((value) => typeof value === 'string' && value.trim()) || '';

    const strengths = normalizeFeedbackItems(
      feedbackResponse.strengths ||
        feedbackResponse.keyStrengths ||
        feedbackResponse.assessment?.strengths ||
        feedbackResponse.assessment?.keyStrengths
    );
    const improvements = normalizeFeedbackItems(
      feedbackResponse.areasForImprovement ||
        feedbackResponse.improvements ||
        feedbackResponse.assessment?.areasForImprovement ||
        feedbackResponse.assessment?.improvements
    );
    const deliveryTips = normalizeFeedbackItems(
      feedbackResponse.deliveryTips ||
        feedbackResponse.voiceDeliveryTips ||
        feedbackResponse.toneAndClarityTips ||
        feedbackResponse.assessment?.deliveryTips
    );
    const strongerAnswers = normalizeFeedbackItems(
      feedbackResponse.whatTheyShouldHaveSaid ||
        feedbackResponse.strongerAnswerExamples ||
        feedbackResponse.moreCompetitiveResponses ||
        feedbackResponse.assessment?.whatTheyShouldHaveSaid
    );
    const encouragement =
      [
        feedbackResponse.encouragement,
        feedbackResponse.assessment?.encouragement,
      ].find((value) => typeof value === 'string' && value.trim()) || '';

    const sections = [];

    if (summary) {
      sections.push(summary.trim());
    }
    if (strengths.length > 0) {
      sections.push(`### Key Strengths\n- ${strengths.join('\n- ')}`);
    }
    if (improvements.length > 0) {
      sections.push(
        `### Improvements To Be More Competitive\n- ${improvements.join('\n- ')}`
      );
    }
    if (deliveryTips.length > 0) {
      sections.push(`### Delivery Tips\n- ${deliveryTips.join('\n- ')}`);
    }
    if (strongerAnswers.length > 0) {
      sections.push(
        `### What You Could Have Said\n- ${strongerAnswers.join('\n- ')}`
      );
    }
    if (encouragement) {
      sections.push(`### Coach's Note\n${encouragement.trim()}`);
    }

    return sections.join('\n\n').trim();
  };

  const normalizeInterviewFeedbackResponse = (feedbackResponse) => {
    if (typeof feedbackResponse === 'string') {
      const text = feedbackResponse.trim();
      return {
        text,
        summary: buildFeedbackSummary(text),
      };
    }

    if (!feedbackResponse || typeof feedbackResponse !== 'object') {
      return {
        text: '',
        summary: '',
      };
    }

    const textCandidates = [
      feedbackResponse.feedback,
      feedbackResponse.feedback_text,
      feedbackResponse.feedbackText,
      feedbackResponse.coachFeedback,
      feedbackResponse.assessment?.feedback,
      feedbackResponse.assessment?.coachFeedback,
      feedbackResponse.assessment?.summary,
    ];
    const directText = textCandidates.find(
      (value) => typeof value === 'string' && value.trim()
    );
    const composedText = buildInterviewFeedbackMarkdown(feedbackResponse);
    const text = (directText || composedText || '').trim();

    const summary =
      [
        feedbackResponse.feedback_summary,
        feedbackResponse.feedbackSummary,
        feedbackResponse.summary,
        feedbackResponse.assessment?.feedbackSummary,
        feedbackResponse.assessment?.summary,
      ].find((value) => typeof value === 'string' && value.trim()) ||
      buildFeedbackSummary(text);

    return {
      text,
      summary: typeof summary === 'string' ? summary.trim() : '',
    };
  };

  const parseBackendResponseBody = async (response) => {
    const rawBody = await response.text();
    if (!rawBody) {
      return null;
    }

    try {
      return JSON.parse(rawBody);
    } catch {
      return rawBody;
    }
  };

  const handleCameraToggle = (enabled) => {
    setIsCameraEnabled(enabled);
  };

  const handleVolumeChange = (volume) => {
    // Just update the state for display purposes
    const numericVolume =
      typeof volume === 'number' ? volume : parseInt(volume, 10);
    if (isNaN(numericVolume) || numericVolume < 0 || numericVolume > 100) {
      console.warn('Invalid volume value:', volume);
      return;
    }

    setAgentVolume(numericVolume);
    console.log('Agent volume display set to:', numericVolume);
  };

  const conversation = useConversation({
    onConnect: () => {
      setStatusMessage('Connected to the AI interviewer.');
      setIsSpeaking(true);
    },
    onDisconnect: async () => {
      setStatusMessage('Interview ended.');
      setIsSpeaking(false);
      setTimerActive(false);
      clearInterval(timerRef.current);

      // If the interview ended unexpectedly, still generate feedback.
      if (interviewStarted && !sessionEnded) {
        try {
          await endInterview({ skipConversationEnd: true });
        } catch (error) {
          console.error(
            'Error finalizing interview after unexpected disconnect:',
            error
          );
        }
      }
    },
    onMessage: (msg) => {
      console.log('msg', msg);
      if (msg.source === 'user') {
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: msg.message },
        ]);
        setTranscript((prev) => [...prev, `User: ${msg.message}`]);
        setIsSpeaking(false);
      } else if (msg.source === 'ai') {
        setMessages((prev) => [...prev, { role: 'ai', content: msg.message }]);
        setTranscript((prev) => [...prev, `AI: ${msg.message}`]);
        setIsSpeaking(true);
      }
    },
    onError: (err) => setStatusMessage(`Error: ${err.message}`),
  });

  // Load interview history from localStorage
  const loadInterviewHistory = () => {
    try {
      const storedHistory = getScopedLocalStorageItem('interviewHistory');
      if (storedHistory) {
        const parsedHistory = JSON.parse(storedHistory);
        // Normalize transcript data for each interview
        const normalizedHistory = parsedHistory.map((interview) => ({
          ...interview,
          transcript: normalizeTranscriptForDisplay(interview.transcript),
          transcript_preview:
            interview.transcript_preview ||
            generateTranscriptPreview(interview.transcript),
        }));
        setInterviewHistory(normalizedHistory);
      } else {
        setInterviewHistory([]);
      }
    } catch (error) {
      console.error(
        'Error loading interview history from localStorage:',
        error
      );
      setInterviewHistory([]);
    }
  };

  // Load history on component mount
  useEffect(() => {
    loadInterviewHistory();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadGuide = async () => {
      setGuideLoading(true);
      try {
        const params = new URLSearchParams();
        if (school) params.set('schoolName', school);
        if (program) params.set('program', program);
        const response = await fetch(
          `${API_BASE_URL}/api/interview-questions?${params.toString()}`,
          { headers: getAuthHeaders() }
        );
        if (!response.ok) {
          throw new Error('Unable to load interview themes');
        }
        const data = await response.json();
        if (!cancelled) {
          setInterviewGuide(data);
        }
      } catch (error) {
        console.error('Error loading interview themes:', error);
        if (!cancelled) {
          setInterviewGuide(null);
        }
      } finally {
        if (!cancelled) {
          setGuideLoading(false);
        }
      }
    };
    loadGuide();
    return () => {
      cancelled = true;
    };
  }, [school, program]);

  // Initialize session time limits on component mount and check for active sessions
  useEffect(() => {
    const initializeSession = async () => {
      const activeSession = getActiveSession();

      if (activeSession && activeSession.type === 'interview') {
        // Page was reloaded during an active interview session
        // The voice connection cannot survive a page reload, so we need to clear the session
        // WITHOUT recording usage (since the call was interrupted, not completed)
        console.log(
          'Detected stale interview session after page reload, clearing without recording usage'
        );

        const maxDuration = activeSession.maxDuration;

        // Set UI state for display
        setMaxSessionTime(maxDuration);
        setTimeRemaining(0);
        setSessionStartTime(new Date(activeSession.startTime));

        // Clear the stale session WITHOUT recording usage
        try {
          clearStaleSession();
        } catch (cleanupError) {
          console.error(
            'Error clearing stale interview session:',
            cleanupError
          );
        }

        // Show a user-friendly message about the interrupted session
        setFeedback({
          sessionInterrupted: true,
          summary:
            'Your interview session was interrupted due to a page reload or connection issue. Please start a new session.',
          areasForImprovement: [
            'Your session was interrupted (page reload or connection lost)',
            'The voice connection could not be maintained',
            'Please start a new session to continue practicing',
          ],
          encouragement:
            'No worries! Start a fresh interview session to continue your practice.',
        });
        setSessionEnded(true);
        setInterviewStarted(false);
        setTimerActive(false);

        return; // Don't continue with normal initialization
      } else {
        // No active session, initialize normally
        const maxDuration = await getMaxSessionDuration();
        const maxSeconds = Math.floor(maxDuration * 60);
        setMaxSessionTime(maxDuration);
        setTimeRemaining(maxSeconds);
      }
    };

    initializeSession();
  }, []);

  useEffect(() => {
    let cancelled = false;
    canStartSession().then((ok) => {
      if (!cancelled) {
        setCanStartInterview(ok);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionEnded, interviewStarted]);

  // Legacy function - keeping for compatibility but not using mock data anymore
  const loadMockData = () => {
    console.warn('Mock data loading disabled - using localStorage only');
    loadInterviewHistory();
  };

  // Backend query function for interview history
  const fetchInterviewHistoryFromBackend = async () => {
    try {
      const user = getStoredUser();
      if (!user?.email) {
        console.warn('No user email found, using local data only');
        return;
      }

      // Build URL with query parameters
      const params = new URLSearchParams({
        userEmail: user.email,
        limit: '20', // Maximum number of interviews to fetch
        include_feedback: 'true',
        include_transcript: 'true', // Include full transcript for detailed view
        include_performance_score: 'true',
        school: school,
      });

      const response = await fetch(
        `${API_BASE_URL}/api/interview-history?${params}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data?.interviews) {
        // Transform backend data to match our frontend structure
        const transformedHistory = data.data.interviews.map((interview) => ({
          id: interview.id,
          date: interview.date,
          duration: interview.duration,
          feedback: interview.feedback,
          feedback_summary:
            interview.feedback_summary ||
            (interview.feedback
              ? interview.feedback.substring(0, 100) + '...'
              : ''),
          transcript: normalizeTranscriptForDisplay(interview.transcript),
          transcript_preview:
            interview.transcript_preview ||
            generateTranscriptPreview(interview.transcript),
          performance: interview.performance || 0,
          questions_count: interview.questions_count || 0,
          user_responses_count: interview.user_responses_count || 0,
          total_speaking_time: interview.total_speaking_time || 0,
        }));

        setInterviewHistory(transformedHistory);
        // Save to localStorage as backup
        setScopedLocalStorageItem(
          'interviewHistory',
          JSON.stringify(transformedHistory)
        );
      } else {
        console.warn('Invalid response structure from backend');
      }
    } catch (error) {
      console.error('Error fetching interview history from backend:', error);
      // Continue with local data if backend fails
    }
  };

  // Enhanced function to show dashboard with data loading
  const showInterviewDashboard = async () => {
    setShowDashboard(true);

    // Try to fetch fresh data from backend
    await fetchInterviewHistoryFromBackend();

    // Scroll to dashboard after a short delay to ensure it's rendered
    setTimeout(() => {
      const dashboardElement = document.querySelector('.dashboard-section');
      if (dashboardElement) {
        dashboardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  // Save interview to history
  const saveInterviewToHistory = (interview) => {
    try {
      // Normalize the transcript for consistency
      const normalizedInterview = {
        ...interview,
        transcript: normalizeTranscriptForDisplay(interview.transcript),
        transcript_preview:
          interview.transcript_preview ||
          generateTranscriptPreview(interview.transcript),
      };

      const updatedHistory = [normalizedInterview, ...interviewHistory].slice(
        0,
        20
      ); // Keep last 20
      setInterviewHistory(updatedHistory);
      setScopedLocalStorageItem(
        'interviewHistory',
        JSON.stringify(updatedHistory)
      );
    } catch (error) {
      console.error('Error saving interview to history:', error);
    }
  };

  // Reset for new interview
  const resetForNewInterview = () => {
    setFeedback('');
    setIsGeneratingFeedback(false);
    setMessages([]);
    setTranscript([]);
    setStatusMessage('');
    setSessionEnded(false);
    setShowDashboard(false);
    setExpandedLatestInterview(false);
    setExpandedInterviews(new Set());
  };

  // Timer effect
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          const newRemaining = prev - 1;

          // Update active session progress every 10 seconds
          if (newRemaining % 10 === 0) {
            const elapsedMinutes = (maxSessionTime * 60 - newRemaining) / 60;
            updateActiveSessionProgress(elapsedMinutes);
          }

          if (newRemaining <= 0) {
            handleTimeUp();
            return 0;
          }
          return newRemaining;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [timerActive, timeRemaining, maxSessionTime]);

  const handleTimeUp = async () => {
    if (!sessionEnded) {
      setTimerActive(false);

      // Check if this was due to subscription limits
      const activeSession = getActiveSession();
      const isSubscriptionLimit =
        activeSession &&
        getActiveSessionElapsedTime() / 60 >=
          Number(getUserVoiceLimits().MAX_SESSION_MINUTES);

      // Send farewell message from agent
      try {
        const sessionMinutes = getUserVoiceLimits().MAX_SESSION_MINUTES;
        const message = isSubscriptionLimit
          ? `I'm sorry, but we've reached your subscription limit of ${sessionMinutes} minutes per session. To continue with longer interviews, please consider upgrading your plan. You did excellent work today!`
          : "I'm sorry, but our time is up for today's interview practice. You performed well! Feel free to come back for another session to continue improving your interview skills. Good luck!";

        await conversation.sendMessage({ message });

        // Wait a moment for the message to be processed, then end
        setTimeout(() => {
          endInterview();
          if (isSubscriptionLimit) {
            setShowUpgradeModal(true);
          }
        }, 3000);
      } catch (error) {
        console.error('Error sending farewell message:', error);
        endInterview();
        if (isSubscriptionLimit) {
          setShowUpgradeModal(true);
        }
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startInterview = async () => {
    // Check if user can start a session
    if (!(await canStartSession())) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Start active session tracking
      const activeSession = startActiveSession('interview');

      const themeSummary = (interviewGuide?.themes || [])
        .map(
          (theme) =>
            `${theme.name}: ${(theme.questions || []).slice(0, 3).join(' | ')}`
        )
        .join(' || ');
      await conversation.startSession({
        agentId,
        dynamicVariables: {
          school_name: interviewGuide?.school || school || 'general graduate admissions',
          program_name: interviewGuide?.program || program || 'graduate program',
          interview_format: interviewGuide?.format || '',
          interview_themes: themeSummary,
        },
      });
      setInterviewStarted(true);
      setTimerActive(true);
      setSessionEnded(false);
      setSessionStartTime(new Date(activeSession.startTime));
    } catch (err) {
      setStatusMessage('Microphone access is required to start the interview.');
      setInterviewStarted(false);
      setTimerActive(false);
      setSessionStartTime(null);
    }
  };

  const endInterview = async (options = {}) => {
    const { skipConversationEnd = false } = options;
    if (sessionEnded) return;

    console.log('Ending interview...');
    setSessionEnded(true);
    setShowExitConfirm(false);
    setTimerActive(false);
    clearInterval(timerRef.current);
    if (!skipConversationEnd) {
      conversation.endSession();
    }

    // Record session for voice minutes tracking
    const elapsedSeconds = maxSessionTime * 60 - timeRemaining;
    const elapsedMinutes = elapsedSeconds / 60;
    await recordSession(elapsedMinutes, 'interview');

    // End active session tracking
    endActiveSession();

    setInterviewStarted(false);
    setIsGeneratingFeedback(true);

    // Save current session data
    const currentSessionData = {
      messages: [...messages],
      transcript: [...transcript],
      duration: elapsedMinutes,
      date: new Date().toISOString(),
    };

    // Generate feedback
    setTimeout(async () => {
      try {
        const feedbackResult = await generateInterviewFeedback();
        const feedbackText = feedbackResult.text;

        console.log('feedback', feedbackText, transcript);
        // Save to history with feedback
        const interviewRecord = {
          id: Date.now(),
          date: new Date().toISOString(),
          duration: elapsedMinutes,
          feedback: feedbackText,
          feedback_summary: feedbackResult.summary,
          transcript: [...transcript],
          performance: calculatePerformanceScore(feedbackText),
        };

        saveInterviewToHistory(interviewRecord);
        setIsGeneratingFeedback(false);

        // Remove automatic dashboard opening - let user manually access it
        // setTimeout(() => {
        //   setShowDashboard(true);
        //   setExpandedLatestInterview(true);

        //   // Scroll to dashboard after a short delay
        //   setTimeout(() => {
        //     const dashboardElement =
        //       document.querySelector('.dashboard-section');
        //     if (dashboardElement) {
        //       dashboardElement.scrollIntoView({
        //         behavior: 'smooth',
        //         block: 'start',
        //       });
        //     }
        //   }, 100);
        // }, 500);
      } catch (error) {
        console.error('Error generating feedback:', error);
        setFeedback(EMPTY_INTERVIEW_FEEDBACK_MESSAGE);

        // Save failed interview to history
        const interviewRecord = {
          id: Date.now(),
          date: new Date().toISOString(),
          duration: elapsedMinutes,
          feedback: EMPTY_INTERVIEW_FEEDBACK_MESSAGE,
          feedback_summary: buildFeedbackSummary(
            EMPTY_INTERVIEW_FEEDBACK_MESSAGE
          ),
          transcript: [...transcript],
          performance: 0,
        };

        saveInterviewToHistory(interviewRecord);
        setIsGeneratingFeedback(false);

        // Remove automatic dashboard opening for failed interviews too
        // setTimeout(() => {
        //   setShowDashboard(true);
        //   setExpandedLatestInterview(true);

        //   setTimeout(() => {
        //     const dashboardElement =
        //       document.querySelector('.dashboard-section');
        //     if (dashboardElement) {
        //       dashboardElement.scrollIntoView({
        //         behavior: 'smooth',
        //         block: 'start',
        //       });
        //     }
        //   }, 100);
        // }, 500);
      }
    }, 3000);
  };

  // Calculate performance score from feedback
  const calculatePerformanceScore = (feedbackText) => {
    if (
      !feedbackText ||
      feedbackText.includes("didn't provide any verbal responses")
    ) {
      return 0;
    }

    // Simple scoring based on keywords (in real app, this would be more sophisticated)
    const positiveKeywords = [
      'excellent',
      'good',
      'well',
      'strong',
      'impressive',
      'clear',
    ];
    const negativeKeywords = ['weak', 'poor', 'unclear', 'improve', 'better'];

    let score = 50; // Base score
    positiveKeywords.forEach((keyword) => {
      if (feedbackText.toLowerCase().includes(keyword)) score += 10;
    });
    negativeKeywords.forEach((keyword) => {
      if (feedbackText.toLowerCase().includes(keyword)) score -= 5;
    });

    return Math.max(0, Math.min(100, score));
  };

  function convertCustomTextToHTML(input) {
    return renderMarkdownToSafeHtml(input);
  }

  // Utility function to parse string transcript into structured format
  const parseTranscriptString = (transcriptString) => {
    if (!transcriptString || typeof transcriptString !== 'string') {
      return [];
    }

    const lines = transcriptString
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const parsedTranscript = [];

    for (const line of lines) {
      if (line.startsWith('AI:')) {
        parsedTranscript.push({
          role: 'ai',
          message: line.replace('AI:', '').trim(),
          timestamp: new Date().toISOString(),
        });
      } else if (line.startsWith('User:')) {
        parsedTranscript.push({
          role: 'user',
          message: line.replace('User:', '').trim(),
          timestamp: new Date().toISOString(),
        });
      }
    }

    return parsedTranscript;
  };

  // Utility function to normalize transcript data for display
  const normalizeTranscriptForDisplay = (transcript) => {
    if (!transcript) return [];

    // If it's already an array of objects, return as-is
    if (Array.isArray(transcript) && transcript.length > 0) {
      // Check if it's array of strings or objects
      if (typeof transcript[0] === 'string') {
        // Convert array of strings to single string and parse
        const transcriptString = transcript.join('\n');
        return parseTranscriptString(transcriptString);
      } else if (transcript[0].role && transcript[0].message) {
        // Already in correct format
        return transcript;
      }
    }

    // If it's a string, parse it
    if (typeof transcript === 'string') {
      return parseTranscriptString(transcript);
    }

    return [];
  };

  // Generate transcript preview from structured data
  const generateTranscriptPreview = (transcript, maxLength = 120) => {
    const normalizedTranscript = normalizeTranscriptForDisplay(transcript);

    if (normalizedTranscript.length === 0) return '';

    let preview = '';
    for (const entry of normalizedTranscript.slice(0, 4)) {
      // First 4 entries
      const speaker = entry.role === 'ai' ? 'AI' : 'User';
      const message =
        entry.message.length > 50
          ? entry.message.substring(0, 50) + '...'
          : entry.message;
      preview += `${speaker}: ${message} `;

      if (preview.length > maxLength) {
        preview = preview.substring(0, maxLength) + '...';
        break;
      }
    }

    return preview;
  };

  const extractInterviewQuestionAnswerPairs = (currentTranscript) => {
    const qaPairs = [];

    for (let i = 0; i < currentTranscript.length; i++) {
      const line = currentTranscript[i];
      if (
        line.startsWith('AI:') &&
        i + 1 < currentTranscript.length &&
        currentTranscript[i + 1].startsWith('User:')
      ) {
        const question = line.replace('AI: ', '').trim();
        const answer = currentTranscript[i + 1].replace('User: ', '').trim();

        if (answer) {
          qaPairs.push({ question, answer });
        }
      }
    }

    return qaPairs;
  };

  const buildInterviewSessionPayload = () => {
    const qaPairs = extractInterviewQuestionAnswerPairs(transcript);
    const user = getStoredUser();
    const actualDurationSeconds = maxSessionTime * 60 - timeRemaining;
    const actualDurationMinutes = actualDurationSeconds / 60;

    // Estimate user speaking time from the number of completed responses.
    const averageResponseTime = 15;
    const estimatedSpeakingTimeMinutes = (qaPairs.length * averageResponseTime) / 60;

    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timestamp: new Date().toISOString(),
    };
    const transcriptPreview = generateTranscriptPreview(transcript, 150);

    return {
      qaPairs,
      payload: {
        answers: qaPairs.map((pair, index) => ({
          text: pair.answer,
          answer: pair.answer,
          index,
        })),
        questions: qaPairs.map((pair, index) => ({
          text: pair.question,
          question: pair.question,
          index,
        })),
        user_email: user?.email,
        sessionDuration: actualDurationSeconds,
        maxSessionTime: maxSessionTime * 60,
        duration_minutes: actualDurationMinutes,
        total_speaking_time_minutes: estimatedSpeakingTimeMinutes,
        session_date:
          sessionStartTime?.toISOString() || new Date().toISOString(),
        completed_at: new Date().toISOString(),
        is_completed: true,
        transcript: transcript.join('\n'),
        transcript_preview: transcriptPreview,
        questions_count: qaPairs.length,
        user_responses_count: qaPairs.length,
        interview_type: 'general',
        school_focus: school || '',
        device_info: JSON.stringify(deviceInfo),
        ai_model_version: 'backend-managed',
        performance_score: null,
        clarity_score: null,
        content_score: null,
        confidence_score: null,
        feedback: null,
        feedback_summary: null,
        session_start_time:
          sessionStartTime?.toISOString() || new Date().toISOString(),
        session_end_time: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        average_response_length:
          qaPairs.reduce((acc, pair) => acc + pair.answer.length, 0) /
          Math.max(qaPairs.length, 1),
        total_words_spoken: qaPairs.reduce(
          (acc, pair) => acc + pair.answer.split(' ').length,
          0
        ),
        session_quality:
          qaPairs.length >= 5 ? 'good' : qaPairs.length >= 3 ? 'fair' : 'limited',
      },
    };
  };

  const saveToBackend = async () => {
    try {
      const { payload } = buildInterviewSessionPayload();

      const response = await fetch(
        `${API_BASE_URL}/api/save-interview-session`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        console.error('Failed to save interview data.');
        return null;
      } else {
        console.log('Interview data saved successfully with enhanced metadata');
      }

      return parseBackendResponseBody(response);
    } catch (error) {
      console.error('Error saving interview data:', error);
      return null;
    }
  };

  const requestInterviewFeedbackFromBackend = async (payload) => {
    const response = await fetch(`${API_BASE_URL}/api/finish-interview-session`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Backend request failed: ${response.status}`);
    }

    return parseBackendResponseBody(response);
  };

  const generateInterviewFeedback = async () => {
    setIsGeneratingFeedback(true);
    try {
      const { qaPairs, payload } = buildInterviewSessionPayload();

      if (qaPairs.length === 0) {
        setFeedback(EMPTY_INTERVIEW_FEEDBACK_MESSAGE);
        return {
          text: EMPTY_INTERVIEW_FEEDBACK_MESSAGE,
          summary: buildFeedbackSummary(EMPTY_INTERVIEW_FEEDBACK_MESSAGE),
        };
      }

      let backendResult;
      try {
        backendResult = await requestInterviewFeedbackFromBackend(payload);
      } catch (error) {
        console.warn(
          'Interview feedback endpoint unavailable, falling back to session save endpoint:',
          error
        );
        backendResult = await saveToBackend();
      }

      const normalizedFeedback = normalizeInterviewFeedbackResponse(backendResult);
      if (!normalizedFeedback.text) {
        throw new Error('Interview backend response did not include feedback');
      }

      setFeedback(normalizedFeedback.text);
      return normalizedFeedback;
    } catch (error) {
      setFeedback(INTERVIEW_FEEDBACK_ERROR_MESSAGE);
      return {
        text: INTERVIEW_FEEDBACK_ERROR_MESSAGE,
        summary: buildFeedbackSummary(INTERVIEW_FEEDBACK_ERROR_MESSAGE),
      };
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  const feedbackText =
    typeof feedback === 'string'
      ? feedback
      : buildInterviewFeedbackMarkdown(feedback);

  return (
    <div className="interview-layout">
      <Sidebar />
      <div className="interview-content">
        <div className="interview-container">
          {!isGeneratingFeedback && !interviewStarted && !feedback && (
            <div className="pre-interview-layout">
              <div className="content-grid">
                <div className="webcam-column">
                  <h1 className="webcam-title">
                    {interviewGuide?.school
                      ? `${interviewGuide.school} mock interview`
                      : 'Graduate mock interview'}
                  </h1>
                  {interviewGuide?.program ? (
                    <p className="interview-program-label">
                      {interviewGuide.program}
                      {interviewGuide.cycle ? ` · ${interviewGuide.cycle}` : ''}
                    </p>
                  ) : null}
                  {isCameraEnabled ? (
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      className="preview-cam"
                    />
                  ) : (
                    <div className="preview-cam disabled">
                      <FiVideoOff size={48} />
                      <p>Camera is off</p>
                    </div>
                  )}

                  <div className="webcam-info">
                    <img src={logoShort} alt="Liwaza" className="webcam-logo" />
                    <strong>Liwaza AI coach is in the call</strong>
                    <p>Max {formatMinutes(maxSessionTime)} session</p>
                  </div>

                  <button
                    onClick={startInterview}
                    className="btn-lwz-primary btn-start-interview"
                    disabled={!canStartInterview}
                  >
                    {canStartInterview
                      ? 'Start Interview'
                      : 'Daily session limit reached'}
                  </button>

                  <button
                    onClick={showInterviewDashboard}
                    className="btn-view-history"
                  >
                    <FiBarChart />
                    View Past Interviews & Performance
                  </button>
                </div>
                <div className="details-column">
                  <VoiceMinutesWidget
                    compact={false}
                    showUpgradeButton={true}
                    onUpgradeClick={() => setShowUpgradeModal(true)}
                    isCameraEnabled={isCameraEnabled}
                    onCameraToggle={handleCameraToggle}
                    onVolumeChange={handleVolumeChange}
                  />
                  <div className="interview-themes-panel">
                    <h2>Themes to prepare</h2>
                    {guideLoading ? (
                      <p className="interview-themes-status">
                        Loading current interview themes…
                      </p>
                    ) : interviewGuide ? (
                      <>
                        {interviewGuide.format ? (
                          <p className="interview-themes-format">
                            {interviewGuide.format}
                          </p>
                        ) : null}
                        <div className="interview-themes-list">
                          {(interviewGuide.themes || []).map((theme) => (
                            <details key={theme.name} className="interview-theme">
                              <summary>{theme.name}</summary>
                              <ul>
                                {(theme.questions || []).map((question) => (
                                  <li key={question}>{question}</li>
                                ))}
                              </ul>
                            </details>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="interview-themes-status">
                        Sign in to see school-specific interview themes.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {interviewStarted && (
            <div className="interview-screen">
              <div
                className="interview-timer"
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background:
                    timeRemaining < 60
                      ? 'var(--liwaza-error-container)'
                      : 'var(--liwaza-primary-container)',
                  color:
                    timeRemaining < 60
                      ? 'var(--liwaza-on-error-container)'
                      : 'var(--liwaza-on-primary-container)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FiClock />
                {formatTime(timeRemaining)}
              </div>

              {/* Voice Minutes Compact Widget during session */}
              <div style={{ position: 'absolute', top: '20px', left: '20px' }}>
                <VoiceMinutesWidget compact={true} showUpgradeButton={false} />
              </div>

              <div className="ai-circle-container">
                <div
                  className={`ai-circle ${isSpeaking ? 'ai-speaking' : ''}`}
                ></div>
              </div>
              <Webcam ref={webcamRef} audio={false} className="webcam-small" />

              <div className="interview-footer">
                <span className="interview-status">
                  {statusMessage} | AI interview
                </span>
                <div className="interview-controls">
                  <button
                    onClick={() => setShowExitConfirm(true)}
                    title="End Interview"
                    disabled={sessionEnded}
                  >
                    <FiPhoneOff />
                  </button>
                  <button
                    onClick={() => setShowHelpModal(true)}
                    title="Help & Support"
                  >
                    <FiAlertCircle />
                  </button>
                </div>
              </div>

              {showExitConfirm && (
                <div className="liwaza-modal-overlay">
                  <div className="liwaza-modal liwaza-modal-interview improved-exit-modal">
                    <div className="exit-modal-header">
                      <div className="exit-modal-icon">
                        <FiPhoneOff />
                      </div>
                      <h3>End Interview Session?</h3>
                    </div>

                    <div className="exit-modal-content">
                      <p className="exit-modal-description">
                        You're about to end your interview session and receive
                        personalized feedback.
                      </p>
                      <div className="exit-modal-warning">
                        <FiAlertCircle />
                        <span>This action cannot be undone</span>
                      </div>
                    </div>

                    <div className="liwaza-modal-actions">
                      <button
                        className="btn-lwz-secondary"
                        onClick={() => setShowExitConfirm(false)}
                      >
                        <span>Continue Interview</span>
                      </button>
                      <button
                        className="btn-lwz-primary exit-confirm-btn"
                        onClick={endInterview}
                      >
                        <FiPhoneOff />
                        <span> End & Get Feedback</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showHelpModal && (
                <div className="liwaza-modal-overlay">
                  <div className="liwaza-modal help-modal">
                    <h3
                      style={{
                        color: 'var(--on-surface, #262730)',
                        marginBottom: '1rem',
                      }}
                    >
                      Help & Support
                    </h3>

                    <div className="liwaza-modal-content-interview">
                      <section className="liwaza-modal-section">
                        <h4>Terms of Use</h4>
                        <p>
                          The AI interviewer is designed to help you practice
                          for your graduate admissions interview. Please note:
                        </p>
                        <ul>
                          <li>
                            Your responses are private and will only be used to
                            provide feedback
                          </li>
                          <li>
                            The AI will ask follow-up questions based on your
                            answers
                          </li>
                          <li>
                            You can retake the interview as many times as needed
                          </li>
                          <li>Be honest and authentic in your responses</li>
                        </ul>
                      </section>

                      <section className="liwaza-modal-section">
                        <h4>Session Limits</h4>
                        <p>Your current plan includes:</p>
                        <ul>
                          <li>
                            {getUserVoiceLimits().MAX_SESSIONS_PER_DAY} scored
                            sessions per day
                          </li>
                          <li>
                            Maximum{' '}
                            {formatMinutes(getUserVoiceLimits().MAX_SESSION_MINUTES)}{' '}
                            per session
                          </li>
                        </ul>
                      </section>

                      <section className="liwaza-modal-section">
                        <h4>Need Support?</h4>
                        <a
                          href="mailto:contact@liwaza.org"
                          className="support-link"
                        >
                          <FiMail /> Contact support at contact@liwaza.org
                        </a>
                      </section>
                    </div>

                    <div className="liwaza-modal-actions">
                      <button
                        className="btn-lwz-primary"
                        onClick={() => setShowHelpModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(feedback || isGeneratingFeedback) && !showDashboard && (
            <div className="feedback-section">
              <div className="feedback-box">
                {isGeneratingFeedback ? (
                  <div className="feedback-loader">
                    <FiLoader className="loader-icon" />
                    <p>Analyzing your interview performance...</p>
                    <p className="loader-subtext">
                      Preparing personalized feedback to boost your interview
                      skills
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="feedback-header">
                      <h2>
                        <FiBarChart />
                        Interview Feedback
                      </h2>
                      {feedbackText &&
                        !feedbackText.includes(
                          "didn't provide any verbal responses"
                        ) && (
                          <div className="performance-score">
                            <FiStar />
                            <span>
                              {calculatePerformanceScore(feedbackText)}/100
                            </span>
                          </div>
                        )}
                    </div>
                    <div
                      className="feedback-content"
                      dangerouslySetInnerHTML={{
                        __html: convertCustomTextToHTML(feedbackText),
                      }}
                    />
                    <div className="feedback-actions">
                      <button
                        className="btn-lwz-primary"
                        onClick={() => {
                          resetForNewInterview();
                        }}
                      >
                        <FiRefreshCw />
                        Try Another Interview
                      </button>
                      <button
                        className="btn-lwz-secondary"
                        onClick={() => setShowDashboard(true)}
                      >
                        <FiBarChart />
                        View Dashboard
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {showDashboard && (
            <div className="dashboard-section">
              <div className="dashboard-container">
                <div className="dashboard-header">
                  <div className="dashboard-title">
                    <FiBarChart />
                    <h2>Interview Dashboard</h2>
                  </div>
                  <button
                    className="btn-lwz-secondary close-dashboard-btn"
                    onClick={() => setShowDashboard(false)}
                  >
                    Back to Feedback
                  </button>
                </div>

                <div className="dashboard-stats">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiCalendar />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {interviewHistory.length}
                      </div>
                      <div className="stat-label">Total Interviews</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiClock />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {Math.round(
                          interviewHistory.reduce(
                            (acc, interview) => acc + (interview.duration || 0),
                            0
                          )
                        )}
                        min
                      </div>
                      <div className="stat-label">Total Practice Time</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">
                      <FiTrendingUp />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {interviewHistory.length > 0
                          ? Math.round(
                              interviewHistory
                                .filter((i) => i.performance > 0)
                                .reduce(
                                  (acc, interview) =>
                                    acc + interview.performance,
                                  0
                                ) /
                                Math.max(
                                  1,
                                  interviewHistory.filter(
                                    (i) => i.performance > 0
                                  ).length
                                )
                            )
                          : 0}
                        %
                      </div>
                      <div className="stat-label">Average Score</div>
                    </div>
                  </div>
                </div>

                <div className="dashboard-actions">
                  <button
                    className="btn-lwz-primary"
                    onClick={() => {
                      resetForNewInterview();
                    }}
                  >
                    <FiPlay />
                    Start New Interview
                  </button>
                </div>

                <div className="interview-history">
                  <h3>Recent Interviews</h3>
                  {interviewHistory.length === 0 ? (
                    <div className="empty-history">
                      <p>
                        No interview history yet. Complete your first interview
                        to see results here!
                      </p>
                    </div>
                  ) : (
                    <div className="history-list">
                      {interviewHistory.slice(0, 10).map((interview, index) => {
                        const isLatest = index === 0;
                        const isExpanded = isLatest
                          ? expandedLatestInterview
                          : expandedInterviews.has(interview.id);

                        const toggleExpansion = (e) => {
                          e.stopPropagation();
                          if (isLatest) {
                            setExpandedLatestInterview(
                              !expandedLatestInterview
                            );
                          } else {
                            const newExpanded = new Set(expandedInterviews);
                            if (newExpanded.has(interview.id)) {
                              newExpanded.delete(interview.id);
                            } else {
                              newExpanded.add(interview.id);
                            }
                            setExpandedInterviews(newExpanded);
                          }
                        };

                        return (
                          <div
                            key={interview.id}
                            className={`history-item ${isLatest ? 'latest-interview' : ''} ${isExpanded ? 'expanded' : ''}`}
                            onClick={toggleExpansion}
                          >
                            {isLatest && (
                              <div className="latest-badge">
                                <FiStar />
                                Latest Interview
                              </div>
                            )}

                            <div className="history-item-header">
                              <div className="history-date">
                                <FiCalendar />
                                {new Date(interview.date).toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }
                                )}
                              </div>
                              <div className="history-score">
                                {interview.performance > 0 ? (
                                  <>
                                    <FiStar />
                                    <span>{interview.performance}/100</span>
                                  </>
                                ) : (
                                  <span className="no-score">No Score</span>
                                )}
                              </div>
                            </div>

                            <div className="history-stats">
                              <div className="history-duration">
                                <FiClock />
                                {Math.round(interview.duration || 0)} min
                              </div>
                              {interview.questions_count && (
                                <div className="history-questions">
                                  <FiAlertCircle />
                                  {interview.questions_count} questions
                                </div>
                              )}
                              {interview.total_speaking_time && (
                                <div className="history-speaking-time">
                                  Speaking:{' '}
                                  {Math.round(interview.total_speaking_time)}{' '}
                                  min
                                </div>
                              )}
                            </div>

                            {/* Show full content for expanded interviews */}
                            {isExpanded ? (
                              <div className="expanded-content">
                                {/* Full Transcript */}
                                <div className="expanded-transcript">
                                  <h5>
                                    <FiAlertCircle />
                                    Complete Interview Transcript
                                  </h5>
                                  <div className="transcript-container-inline">
                                    {(() => {
                                      const normalizedTranscript =
                                        normalizeTranscriptForDisplay(
                                          interview.transcript
                                        );
                                      return normalizedTranscript.length > 0 ? (
                                        normalizedTranscript.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className={`transcript-entry-inline ${entry.role}`}
                                            >
                                              <div className="speaker-label">
                                                {entry.role === 'ai'
                                                  ? 'AI Interviewer'
                                                  : 'You'}
                                              </div>
                                              <div className="message-content">
                                                {entry.message}
                                              </div>
                                            </div>
                                          )
                                        )
                                      ) : (
                                        <div className="no-transcript">
                                          <FiMessageSquare />
                                          <span>
                                            No transcript available for this
                                            interview.
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>

                                {/* Full Feedback */}
                                <div className="expanded-feedback">
                                  <h5>
                                    <FiBarChart />
                                    Complete AI Feedback
                                  </h5>
                                  <div
                                    className="feedback-content-inline"
                                    dangerouslySetInnerHTML={{
                                      __html: convertCustomTextToHTML(
                                        interview.feedback ||
                                          'No feedback available.'
                                      ),
                                    }}
                                  />
                                </div>

                                <div className="expanded-actions">
                                  <button
                                    className="btn-lwz-secondary collapse-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isLatest) {
                                        setExpandedLatestInterview(false);
                                      } else {
                                        const newExpanded = new Set(
                                          expandedInterviews
                                        );
                                        newExpanded.delete(interview.id);
                                        setExpandedInterviews(newExpanded);
                                      }
                                    }}
                                  >
                                    Collapse Details
                                  </button>
                                  <button
                                    className="btn-lwz-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      resetForNewInterview();
                                    }}
                                  >
                                    <FiRefreshCw />
                                    Practice Again
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* Regular preview content */}
                                <div className="history-feedback-preview">
                                  <strong>Feedback Summary:</strong>
                                  <p>
                                    {interview.feedback_summary ||
                                      (interview.feedback &&
                                      interview.feedback.length > 120
                                        ? interview.feedback.substring(0, 120) +
                                          '...'
                                        : interview.feedback)}
                                  </p>
                                </div>

                                {interview.transcript_preview && (
                                  <div className="history-transcript-preview">
                                    <strong>Conversation Snippet:</strong>
                                    <p>{interview.transcript_preview}</p>
                                  </div>
                                )}

                                <div className="history-item-footer">
                                  <span className="view-details-hint">
                                    Expand for full details
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Upgrade Modal */}
          {showUpgradeModal && (
            <div
              className="liwaza-modal-overlay"
              onClick={(e) => {
                // Only close if clicking on the overlay itself
                if (e.target === e.currentTarget) {
                  setShowUpgradeModal(false);
                }
              }}
            >
              <div
                className="liwaza-modal upgrade-modal"
                onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside
              >
                <div className="upgrade-modal-header">
                  <h3>Upgrade Your Voice Practice</h3>
                  <button
                    className="interview-modal-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUpgradeModal(false);
                    }}
                  >
                    ×
                  </button>
                </div>

                <div className="upgrade-modal-content">
                  <div className="upgrade-message">
                    <p>
                      You've used all speaking sessions for today. New sessions
                      open again at midnight, Africa/Douala time.
                    </p>
                  </div>

                  <div className="current-plan">
                    <h4>Current Plan: Basic</h4>
                    <div className="current-plan-limits">
                      <div>
                        {getUserVoiceLimits().MAX_SESSIONS_PER_DAY} sessions / day
                      </div>
                      <div>
                        {formatMinutes(getUserVoiceLimits().MAX_SESSION_MINUTES)} per
                        session
                      </div>
                    </div>
                  </div>

                  <div className="upgrade-options">
                    <div className="upgrade-plan">
                      <div className="plan-header">
                        <h4>Premium</h4>
                        <div className="plan-price">$19.99/month</div>
                      </div>
                      <div className="plan-features">
                        <div className="plan-feature">
                          ✓ 120 minutes per week
                        </div>
                        <div className="plan-feature">
                          ✓ 15 minutes per session
                        </div>
                        <div className="plan-feature">✓ Unlimited sessions</div>
                        <div className="plan-feature">✓ Advanced feedback</div>
                        <div className="plan-feature">✓ Priority support</div>
                      </div>
                      <button
                        className="btn-lwz-primary plan-select-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle plan selection here
                          console.log('Selected Premium plan');
                          // You can add actual subscription logic here
                        }}
                      >
                        Choose Premium
                      </button>
                    </div>

                    <div className="upgrade-plan">
                      <div className="plan-header">
                        <h4>Pro</h4>
                        <div className="plan-price">$39.99/month</div>
                      </div>
                      <div className="plan-features">
                        <div className="plan-feature">
                          ✓ 300 minutes per week
                        </div>
                        <div className="plan-feature">
                          ✓ 30 minutes per session
                        </div>
                        <div className="plan-feature">✓ Unlimited sessions</div>
                        <div className="plan-feature">✓ Detailed analytics</div>
                        <div className="plan-feature">✓ Custom AI coaching</div>
                        <div className="plan-feature">✓ Premium support</div>
                      </div>
                      <button
                        className="btn-lwz-primary plan-select-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle plan selection here
                          console.log('Selected Pro plan');
                          // You can add actual subscription logic here
                        }}
                      >
                        Choose Pro
                      </button>
                    </div>
                  </div>
                </div>

                <div className="upgrade-modal-footer">
                  <button
                    className="btn-lwz-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUpgradeModal(false);
                    }}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Interview;
